import {
  Drawer,
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import type { NavigationContractItem, RouteId } from '@finance/contracts';
import { resolveIcon } from '../../runtime/icons';
import { resolveTKey } from '../../i18n/resolve-key';

interface MobileNavigationDrawerProps {
  items: NavigationContractItem[];
  activeRouteId: RouteId | false;
  open: boolean;
  onClose: () => void;
  onNavigate: (routeId: RouteId) => void;
  title: string;
}

function isActionable(item: NavigationContractItem): boolean {
  return item.availability === 'AVAILABLE' || item.availability === 'READ_ONLY';
}

export function MobileNavigationDrawer({
  items,
  activeRouteId,
  open,
  onClose,
  onNavigate,
  title,
}: MobileNavigationDrawerProps) {
  const { t } = useTranslation();
  const active = activeRouteId === false ? '' : activeRouteId;

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      slotProps={{ paper: { sx: { width: { xs: '80vw', sm: 320 }, maxWidth: 320 } } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Typography variant="h6" component="p">
          {title}
        </Typography>
        <IconButton onClick={onClose} aria-label={t('common:close')} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box component="nav" aria-label={title} data-testid="mobile-navigation" sx={{ overflowY: 'auto', flex: 1 }}>
        <List>
          {items.map((item) => {
            const Icon = resolveIcon(item.iconKey);
            const actionable = isActionable(item);
            return (
              <ListItemButton
                key={item.routeId}
                data-testid={`mobile-nav-item-${item.routeId}`}
                selected={item.routeId === active}
                disabled={!actionable}
                onClick={() => {
                  if (actionable) {
                    onNavigate(item.routeId);
                  }
                }}
                sx={{ '&.Mui-selected': { bgcolor: 'action.selected' } }}
              >
                {Icon && (
                  <ListItemIcon>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                )}
                <ListItemText primary={t(resolveTKey(item.labelKey))} />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
}
